import React from "react";
import { Landmark, Banknote, TrendingUp, Gauge } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { PortfolioMetrics } from "@/types/dashboard";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PortfolioOverviewProps {
  portfolio: PortfolioMetrics;
  leverageDistribution?: Record<string, number>;
  isLoading: boolean;
}

const formatCurrency = (value: number): string => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const LEVERAGE_COLORS: Record<string, string> = {
  "0-10%": "#2e7d32",
  "10-20%": "#1976d2",
  "20-30%": "#ed6c02",
  "30%+": "#d32f2f",
};

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  isLoading: boolean;
}> = ({ title, value, icon, color, isLoading }) => (
  <Card className="h-full">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="mb-1 text-sm text-muted-foreground">{title}</p>
          {isLoading ? (
            <Skeleton className="h-10 w-24" />
          ) : (
            <p className="text-2xl font-semibold" style={{ color }}>
              {value}
            </p>
          )}
        </div>
        <div
          className="flex h-10 w-10 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${color}26`, color }}
        >
          {icon}
        </div>
      </div>
    </CardContent>
  </Card>
);

const PortfolioOverview: React.FC<PortfolioOverviewProps> = ({
  portfolio,
  leverageDistribution,
  isLoading,
}) => {
  const navigate = useNavigate();
  const repaymentDeals = portfolio.repaymentDeals;

  const leverageData = leverageDistribution
    ? Object.entries(leverageDistribution).map(([range, count]) => ({
        range,
        count,
        fill: LEVERAGE_COLORS[range] || "#1976d2",
      }))
    : [];

  return (
    <div>
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Active Repayments"
          value={portfolio.activeRepayments}
          icon={<Landmark className="h-6 w-6" />}
          color="#1976d2"
          isLoading={isLoading}
        />
        <MetricCard
          title="Total Funded"
          value={formatCurrency(portfolio.totalFunded)}
          icon={<Banknote className="h-6 w-6" />}
          color="#2e7d32"
          isLoading={isLoading}
        />
        <MetricCard
          title="Est. Daily Collections"
          value={formatCurrency(portfolio.dailyCollections)}
          icon={<TrendingUp className="h-6 w-6" />}
          color="#ed6c02"
          isLoading={isLoading}
        />
        <MetricCard
          title="Avg Factor Rate"
          value={isLoading ? "..." : portfolio.avgFactorRate.toFixed(2)}
          icon={<Gauge className="h-6 w-6" />}
          color="#9c27b0"
          isLoading={isLoading}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-12">
        <Card className="flex max-h-[420px] flex-col overflow-hidden lg:col-span-7">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-bold">Active Repayment Deals</h3>
          </div>
          {isLoading ? (
            <div className="p-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="mb-2 h-12 w-full" />
              ))}
            </div>
          ) : repaymentDeals.length > 0 ? (
            <ScrollArea className="max-h-[360px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead className="text-right">Offer</TableHead>
                    <TableHead className="text-right">Daily</TableHead>
                    <TableHead className="text-right">Factor</TableHead>
                    <TableHead className="text-right">Term</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {repaymentDeals.slice(0, 10).map((deal) => (
                    <TableRow
                      key={deal.borrower_id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/applications/${deal.borrower_id}`)}
                    >
                      <TableCell className="font-medium text-primary">
                        {deal.company_name || `#${deal.borrower_id}`}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(deal.offer_amount ?? 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        ${(deal.daily_payment ?? 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {deal.factor_rate?.toFixed(2) ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {deal.term_days ? `${deal.term_days}d` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          ) : (
            <div className="p-6 text-center">
              <p className="text-sm italic text-muted-foreground">No active repayment deals</p>
            </div>
          )}
        </Card>

        <Card className="lg:col-span-5">
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-bold">Leverage Distribution</h3>
            {isLoading ? (
              <Skeleton className="h-[280px] w-full rounded-md" />
            ) : leverageData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={leverageData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="range" tick={{ fontSize: 12 }} axisLine={{ stroke: "#e0e0e0" }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    axisLine={{ stroke: "#e0e0e0" }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "none",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    }}
                    formatter={(value: number) => [value, "Deals"]}
                  />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={48}>
                    {leverageData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[280px] items-center justify-center">
                <p className="text-sm italic text-muted-foreground">No leverage data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PortfolioOverview;
